import { useFormControlProps } from '@chakra-ui/react';
import { useCallback } from 'react';
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
} from 'react-international-phone';

import {
  Flex,
  Icon,
  InputLeftElement,
  Select,
  SelectComponents,
  SelectProps,
  Text,
} from '@frontend/components';

import { usePhoneInputContext } from './usePhoneInputContext';

export const COUNTRY_SELECTOR_WIDTH = 6; // chakra units

const DropdownIndicator = (props) => (
  <SelectComponents.DropdownIndicator
    {...props}
    innerProps={{ ...props.innerProps, boxSize: 2.25 }}
  />
);

const Option = (props) => (
  <SelectComponents.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      display: 'flex',
      gap: 0.5,
      justifyContent: 'start',
      alignItems: 'center',
    }}
  />
);

const SingleValue = (props) => {
  const [selectedOption] = props.getValue();
  return <Icon as={FlagImage} iso2={selectedOption?.value} boxSize={2.5} />;
};

const ValueContainer = (props) => (
  <SelectComponents.ValueContainer
    {...props}
    innerProps={{
      ...props.innerProps,
      flexWrap: 'nowrap',
      paddingInlineStart: 0.75,
      paddingInlineEnd: 0,
    }}
  />
);

type CountryOption = {
  value: CountryIso2;
  label: string;
  dialCode: string;
};

const OptionLabel = ({ value, label, dialCode }: CountryOption) => {
  return (
    <>
      <Icon as={FlagImage} iso2={value} width={2.5} height={3} />
      <Flex gap={1} overflow="hidden">
        <Text
          as="span"
          flexShrink={1}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {label}
        </Text>
        <Text as="span" flexShrink={0} color="feature.secondary">
          +{dialCode}
        </Text>
      </Flex>
    </>
  );
};

export const PhoneInputCountrySelect = ({
  onChange,
  ...restProps
}: SelectProps) => {
  const { country, setCountry, inputWidth, stateProps } =
    usePhoneInputContext();

  const controlProps = useFormControlProps({ ...stateProps, ...restProps });

  const { isDisabled, isInvalid, isReadOnly, ...restControlProps } =
    controlProps;

  const Control = useCallback(
    (props) => (
      <SelectComponents.Control
        {...props}
        innerProps={{
          ...props.innerProps,
          background: 'transparent',
          ...(isInvalid && { borderColor: 'alert.error' }),
        }}
      />
    ),
    [isInvalid],
  );

  const MenuList = useCallback(
    (props) => (
      <SelectComponents.MenuList
        {...props}
        innerProps={{ ...props.innerProps, minWidth: inputWidth }}
      />
    ),
    [inputWidth],
  );

  return (
    <InputLeftElement width={COUNTRY_SELECTOR_WIDTH}>
      <Select
        isClearable={false}
        isMulti={false}
        isDisabled={isDisabled || isReadOnly}
        components={{
          Control,
          DropdownIndicator,
          IndicatorSeparator: null,
          MenuList,
          Option,
          SingleValue,
          ValueContainer,
        }}
        options={defaultCountries.map((country) => {
          const { iso2, name, dialCode } = parseCountry(country);
          return { value: iso2, label: name, dialCode };
        })}
        value={{
          value: country.iso2,
          label: country.name,
        }}
        onChange={(option: CountryOption, action) => {
          setCountry(option.value);
          onChange?.(option, action);
        }}
        formatOptionLabel={(option: CountryOption) => (
          <OptionLabel {...option} />
        )}
        {...restControlProps}
      />
    </InputLeftElement>
  );
};
